import { Suspense, useContext } from "react";

import { type Provider, Sidebar, type NavItem } from "@stacklet/ui";
import { graphql } from "react-relay";
import { useFragment } from "react-relay/hooks";
import { useLocation, useNavigate } from "react-router";

import { ProviderSelector } from "app/components/ProviderSelector";
import { paths } from "app/constants/paths";
import { useConfig } from "app/contexts";
import { SideBarContext } from "app/contexts/SideBarContext";
import { useEnabledDashboards } from "app/hooks/feature-flags/useEnabledDashboards/useEnabledDashboards";

import { NavBarUserMenu } from "../NavBarUserMenu";
import { ChatThreadsList } from "./ChatThreadsList";
import { useNavItems } from "./hooks";
import { Jun0Icon } from "./Jun0Icon";

import type { SideBarWrapper_query$key } from "./__generated__/SideBarWrapper_query.graphql";

interface Props {
  onProviderChange: (provider: Provider) => void;
  query: SideBarWrapper_query$key;
}

export function SideBarWrapper({ onProviderChange, query }: Props) {
  const config = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const { collapsed, toggleCollapsed } = useContext(SideBarContext);

  const data = useFragment(
    graphql`
      fragment SideBarWrapper_query on Query
      @argumentDefinitions(filterElement: { type: "FilterElementInput" }) {
        UrlConfig {
          jun0
          redash
          sinistral
          superset
        }
        ...NavBarUserMenu_query
        ...useEnabledDashboards_query @arguments(filterElement: $filterElement)
        ...ChatThreadsList_query
      }
    `,
    query,
  );

  const { jun0, redash, sinistral, superset } = data.UrlConfig ?? {};
  const appSelectorOptions = [
    ...(redash ? [{ label: "AssetDB", href: redash, isBeta: false }] : []),
    // Superset is plain "AssetDB" once the Redash-backed one is gone
    ...(superset
      ? [
          {
            label: redash ? "AssetDB (Preview)" : "AssetDB",
            href: superset,
            isBeta: false,
          },
        ]
      : []),
    { label: "Console", href: "", isBeta: false },
    ...(sinistral
      ? [{ label: "IaC Governance", href: sinistral, isBeta: false }]
      : []),
  ];

  const enabledDashboards = useEnabledDashboards(data);
  const baseNavItems = useNavItems(enabledDashboards, config);

  const chatsNavItem: NavItem[] = jun0
    ? [
        {
          label: "Jun0 AI",
          id: "chats",
          Icon: Jun0Icon,
          chip: "New",
          mainPath: paths.jun0,
          popoutContent: (
            <Suspense fallback={null}>
              <ChatThreadsList
                onNewChat={() => navigate(paths.jun0)}
                onThreadClick={(uuid) =>
                  navigate(`${paths.jun0}?threadId=${uuid}`)
                }
                queryRef={data}
              />
            </Suspense>
          ),
        },
      ]
    : [];

  const navItems = [...chatsNavItem, ...baseNavItems];

  return (
    <Sidebar
      ProviderSelector={
        <ProviderSelector
          className="px-md py-xxs"
          onChange={onProviderChange}
        />
      }
      SignInMenu={<NavBarUserMenu queryRef={data} />}
      appSelectorOptions={appSelectorOptions}
      collapsed={collapsed}
      handleCollapsed={toggleCollapsed}
      navItems={navItems}
      navigate={navigate}
      pathname={location.pathname}
      selectedApp="Console"
      collapsible
    />
  );
}
